// Español para los códigos que guarda el módulo de Entidades. Los códigos son en
// inglés (convención del backend); todo lo que el gestor ve en pantalla se
// traduce aquí, de modo que ningún Chip ni ningún mensaje muestre el código
// crudo.
import {
  ENTITY_DOC_FILE_STATUS,
  ENTITY_DOC_STATUS,
  ENTITY_REGISTRATION_SOURCE,
  ENTITY_STATUS,
  ENTITY_TYPE,
  PHONE_TYPE,
} from "../../constants";
import { EXPIRED } from "./entityLogic";

export const ENTITY_TYPE_LABELS = {
  [ENTITY_TYPE.PUBLIC]: "Pública",
  [ENTITY_TYPE.PRIVATE]: "Privada",
  [ENTITY_TYPE.MIXED]: "Mixta",
  [ENTITY_TYPE.NONPROFIT]: "Sin ánimo de lucro",
  [ENTITY_TYPE.INTERNATIONAL]: "Organismo internacional",
};

export const ENTITY_STATUS_LABELS = {
  [ENTITY_STATUS.PENDING]: "Pendiente de aprobación",
  [ENTITY_STATUS.ACTIVE]: "Activa",
  [ENTITY_STATUS.INACTIVE]: "Inactiva",
};

export const ENTITY_STATUS_COLOR = {
  [ENTITY_STATUS.PENDING]: "warning",
  [ENTITY_STATUS.ACTIVE]: "success",
  [ENTITY_STATUS.INACTIVE]: "default",
};

export const ENTITY_REGISTRATION_SOURCE_LABELS = {
  [ENTITY_REGISTRATION_SOURCE.PUBLIC]: "Registro público",
  [ENTITY_REGISTRATION_SOURCE.STAFF]: "Registro interno",
};

// Naturaleza de un teléfono. Importa en pantalla porque decide si se muestra el
// botón «Verificar» (un fijo no recibe SMS) o el campo de extensión.
export const PHONE_TYPE_LABELS = {
  [PHONE_TYPE.MOBILE]: "Celular",
  [PHONE_TYPE.LANDLINE]: "Teléfono fijo",
};

// Estado de UN archivo. EXPIRED no es un estado guardado: lo deriva
// entityLogic.fileEffectiveStatus a partir de la vigencia y la fecha de hoy.
export const DOC_FILE_STATUS_LABELS = {
  [ENTITY_DOC_FILE_STATUS.PENDING]: "Pendiente",
  [ENTITY_DOC_FILE_STATUS.UPLOADED]: "Cargado",
  [ENTITY_DOC_FILE_STATUS.VALIDATED]: "Validado",
  [ENTITY_DOC_FILE_STATUS.REJECTED]: "Rechazado",
  [EXPIRED]: "Vencido",
};

export const DOC_FILE_STATUS_COLOR = {
  [ENTITY_DOC_FILE_STATUS.PENDING]: "default",
  [ENTITY_DOC_FILE_STATUS.UPLOADED]: "info",
  [ENTITY_DOC_FILE_STATUS.VALIDATED]: "success",
  [ENTITY_DOC_FILE_STATUS.REJECTED]: "error",
  [EXPIRED]: "warning",
};

// Estado de la documentación completa de la entidad.
export const ENTITY_DOC_STATUS_LABELS = {
  [ENTITY_DOC_STATUS.PENDING]: "Sin documentos",
  [ENTITY_DOC_STATUS.INCOMPLETE]: "Incompleta",
  [ENTITY_DOC_STATUS.COMPLETE]: "Completa",
  [ENTITY_DOC_STATUS.VALIDATED]: "Validada",
};

export const ENTITY_DOC_STATUS_COLOR = {
  [ENTITY_DOC_STATUS.PENDING]: "default",
  [ENTITY_DOC_STATUS.INCOMPLETE]: "warning",
  [ENTITY_DOC_STATUS.COMPLETE]: "info",
  [ENTITY_DOC_STATUS.VALIDATED]: "success",
};

// Códigos de validación → texto accionable. Los produce entityLogic (en el
// formulario) y también los devuelve handleEntity, así que una regla que se
// valida en ambos lados se lee igual venga de donde venga.
export const ENTITY_ERROR_LABELS = {
  NIT_INVALID: "El NIT debe tener entre 5 y 15 dígitos.",
  LEGAL_NAME_REQUIRED: "Escribe la razón social de la entidad.",
  INVALID_ENTITY_TYPE: "Selecciona la naturaleza jurídica de la entidad.",
  INVALID_STATUS: "Selecciona el estado de la entidad.",
  INVALID_EMAIL: "El correo no tiene un formato válido.",
  INVALID_EXPIRY_DATE: "La fecha de vigencia no es válida.",
  INVALID_PHONE: "El número no está completo.",
  DOC_FILE_REQUIRED: "Falta adjuntar este documento.",
  EXPIRY_DATE_REQUIRED: "Indica hasta cuándo está vigente.",
  ISSUE_DATE_REQUIRED: "Indica la fecha de expedición del documento.",
  INVALID_ISSUE_DATE: "La fecha de expedición no es válida.",
  ISSUE_DATE_IN_FUTURE: "La expedición no puede ser posterior a hoy.",
  EMAIL_NOT_VERIFIED: "Verifica el correo antes de continuar.",
  PHONE_NOT_VERIFIED: "Verifica el celular antes de continuar.",
  // Al pie del campo va la versión corta; la larga, con la salida, se pinta en
  // el aviso de arriba (ENTITY_REQUEST_ERROR_LABELS).
  EMAIL_HAS_ACCOUNT: "Ese correo ya tiene una cuenta en la aplicación.",
  TERMS_REQUIRED: "Debes autorizar el tratamiento de los datos para continuar.",
  REQUIRED: "Este dato es obligatorio.",
};

// El NOMBRE de cada campo, para poder decir «falta la razón social» en un aviso
// que resume todo lo que quedó pendiente. El helperText de cada campo dice QUÉ
// le pasa; este mapa dice DE QUIÉN se está hablando.
export const ENTITY_FIELD_LABELS = {
  nit: "NIT",
  legalName: "Razón social",
  entityType: "Naturaleza jurídica",
  status: "Estado",
  email: "Correo de la entidad",
  phone: "Celular de la entidad",
  terms: "Autorización de tratamiento de datos",
  legalRepName: "Nombre del representante legal",
  legalRepIdType: "Tipo de documento del representante",
  legalRepIdNumber: "Número de documento del representante",
  legalRepEmail: "Correo del representante legal",
  legalRepPhone: "Teléfono del representante legal",
  contactEmails: "Correos de contacto",
  contactPhones: "Teléfonos de contacto",
  // Los datos de quien registra la entidad (paso «Tus datos» del registro
  // público). Con ellos se crea su cuenta, así que sin ellos no hay con qué
  // entrar a la aplicación.
  founderName: "Nombres",
  founderLastName: "Apellidos",
  founderNuip: "Número de documento",
};

// Errores que devuelve la función y que no son de un campo concreto.
export const ENTITY_REQUEST_ERROR_LABELS = {
  ALREADY_EXISTS: "Ya existe una entidad registrada con ese NIT.",
  NOT_FOUND: "La entidad ya no existe. Recarga la página.",
  FORBIDDEN: "No tienes permiso para realizar esta acción.",
  VALIDATION_ERROR: "Revisa los campos marcados.",
  ENTITY_HAS_AGREEMENTS:
    "No se puede eliminar: la entidad tiene convenios registrados. Márcala como inactiva.",
  ALREADY_VALIDATED:
    "Ese documento ya fue validado. Para rechazarlo, primero debe cargarse uno nuevo.",
  COMMENTS_REQUIRED: "Para rechazar un documento hay que explicar por qué (mínimo 5 caracteres).",
  UNKNOWN_DOC_TYPE: "Ese documento no está en el catálogo de la configuración.",
  UNKNOWN_REPLACE_TARGET: "El archivo que intentas reemplazar ya no existe. Recarga la página.",
  DOC_AT_MAX_FILES: "Ya alcanzaste el número máximo de archivos para ese documento.",
  UPLOAD_FAILED: "No fue posible cargar el archivo. Inténtalo de nuevo.",
  NETWORK: "No fue posible conectar con el servidor.",

  // Aprobación
  NOT_PENDING: "Esa entidad ya fue revisada. Recarga la página para ver su estado actual.",
  REASON_REQUIRED: "Para rechazar una entidad hay que explicar por qué (mínimo 5 caracteres).",

  // Contactos
  PRIMARY_CONTACT_REQUIRED:
    "No se puede eliminar el contacto principal. Marca otro como principal y vuelve a intentarlo.",
  CONTACT_NOT_VERIFIED:
    "Sólo un contacto verificado puede ser el principal: es por donde salen los avisos y por donde se entra al portal.",
  CONTACT_NOT_FOUND: "Ese contacto ya no existe. Recarga la página.",
  UNKNOWN_CONTACT_ACTION: "Acción de contacto no reconocida.",
  EMAIL_REQUIRED: "La entidad necesita al menos un correo.",
  INVALID_PHONE: "El celular no tiene un formato válido.",
  EMAIL_IN_USE: "Ese correo ya está registrado en otra entidad.",
  EMAIL_IS_STAFF:
    "Ese correo es de una cuenta interna de la institución. Los contactos de la "
    + "empresa son de la empresa: usa un correo suyo.",
  EMAIL_HAS_ACCOUNT:
    "Ese correo ya tiene una cuenta en la aplicación, y una cuenta no puede ser "
    + "de una persona de la institución y de una empresa a la vez. Registra la "
    + "empresa con otro correo; si ya hace parte de una empresa registrada, "
    + "pídele a quien la administra que te invite.",
  PHONE_IN_USE: "Ese celular ya está registrado en otra entidad.",
  CONTACT_NAME_REQUIRED:
    "Escribe a nombre de quién va este contacto. Si es de alguien de tu equipo, selecciónalo en la lista.",
  MEMBER_NOT_FOUND: "Esa persona ya no hace parte de tu equipo. Recarga la página.",
  MEMBER_EMAIL_EXISTS:
    "Cada persona tiene un solo correo, que es con el que entra a la aplicación. Para cambiárselo usa «Cambiar correo».",
  MEMBER_EMAIL_REQUIRED:
    "Ese correo es con el que esa persona entra a la aplicación y no se puede eliminar. Si ya no hace parte del equipo, retírala desde Personal.",
  MEMBER_MOBILE_REQUIRED:
    "Cada persona del equipo necesita un celular verificado. Los fijos se pueden agregar encima, o como contacto externo.",

  // Registro público
  NIT_ALREADY_REGISTERED:
    "Ya hay una entidad registrada con ese NIT. Usa «Continuar mi registro» para entrar a su ficha.",
  EMAIL_NOT_VERIFIED: "Verifica el correo antes de continuar.",
  PHONE_NOT_VERIFIED: "Verifica el celular antes de continuar.",
  EXTRA_CONTACTS_ON_REGISTRATION:
    "En el registro se pide un correo y un celular. Los demás contactos se agregan después, desde tu ficha.",
  MOBILE_REQUIRED:
    "Para registrarte necesitas un celular: a un teléfono fijo no le podemos enviar el código de verificación.",
  EXPIRY_DATE_REQUIRED: "Ese documento tiene vigencia: indica hasta cuándo está vigente.",
  ISSUE_DATE_REQUIRED:
    "Ese documento se cuenta desde que lo expidieron: indica su fecha de expedición.",
  INVALID_ISSUE_DATE: "La fecha de expedición no es válida.",
  ISSUE_DATE_IN_FUTURE: "La fecha de expedición no puede ser posterior a hoy.",
  ENCRYPTED_FILE: "El archivo está protegido con contraseña. Cárgalo sin protección.",
  FILE_TOO_LARGE: "El archivo supera el tamaño máximo permitido.",
  UNSUPPORTED_MIME: "Ese tipo de archivo no está permitido. Usa PDF o una imagen.",
  UNSUPPORTED_LOGO_MIME: "El logo debe ser una imagen.",
  LOGO_TOO_LARGE: "El logo supera los 2 MB.",
  FILE_META_MISMATCH: "No fue posible procesar los archivos. Vuelve a intentarlo.",
  UNKNOWN_DOCUMENT: "Ese documento no aplica a la naturaleza jurídica de tu entidad.",
  RATE_LIMIT_IP: "Demasiados intentos desde esta conexión. Espera unos minutos.",
  RATE_LIMIT_EMAIL: "Demasiados intentos con ese correo. Espera unos minutos.",
  OTP_INVALID: "El código no es correcto.",
  OTP_EXPIRED: "El código venció. Solicita uno nuevo.",
  OTP_MAX_ATTEMPTS: "Superaste los intentos permitidos. Solicita un código nuevo.",
  NO_ENTITY: "No hay una entidad asociada a ese contacto.",
};

// Traduce un código, dejándolo tal cual si no está mapeado (nunca oculta datos).
export const labelForCode = (map, code, fallback = "—") => map[code] || code || fallback;

// El texto de un código de CAMPO. Se buscan los dos mapas porque el mismo código
// puede llegar de la validación local o de la función —EXPIRY_DATE_REQUIRED, por
// ejemplo, lo produce el formulario y también el servidor— y quien lo pinta no
// tiene por qué saber cuál de los dos lo mandó.
export const fieldErrorLabel = (code) =>
  code ? ENTITY_ERROR_LABELS[code] || ENTITY_REQUEST_ERROR_LABELS[code] || code : null;

// «NIT, Razón social» — el resumen de lo que falta, a partir del mapa
// campo → código que devuelve la validación.
export const missingFieldsText = (errors, extraLabels = {}) =>
  Object.keys(errors || {})
    .map((k) => extraLabels[k] || ENTITY_FIELD_LABELS[k] || k)
    .filter(Boolean)
    .join(", ");

// "15 ene 2026" a partir de una fecha ISO date-only, sin pasar por la zona
// horaria local (que restaría un día a las fechas guardadas como YYYY-MM-DD).
export const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00Z`);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

// Fecha y hora de una marca de tiempo ISO — para la bitácora y el sello de quien
// validó.
export const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Bogota",
  });
};

// Tamaño legible de un archivo.
export const formatBytes = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
